// Demo page: NextAdmin /auth/sign-in card frame (AuthLayout) with the /mail-success outcome pattern for the result.
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle, XCircle } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { acceptInvitationMutation, previewInvitationOptions } from '../generated/api/@tanstack/react-query.gen.js';
import { AuthLayout } from '../components/page/AuthLayout.js';
import { buttonStyles } from '../components/tailgrids/core/button.js';
import { Spinner } from '../components/tailgrids/core/spinner/index.js';
import { Alert, AlertContent, AlertIndicator, AlertTitle } from '../components/tailgrids/core/alert.js';
import { Button } from '../components/ui/index.js';
import { useAuthStore } from '../store/index.js';

const ROLE_LABELS = {
  org_admin: 'Organization admin',
  trader: 'Trader',
  analyst: 'Analyst',
  viewer: 'Viewer',
} as const;

function Outcome({ tone, children }: { tone: 'success' | 'danger'; children: React.ReactNode }) {
  const Icon = tone === 'success' ? CheckCircle : XCircle;
  const colours = tone === 'success' ? 'bg-success-muted text-success-text' : 'bg-danger-muted text-danger-text';
  return (
    <div className="flex flex-col items-center gap-6">
      <span className={`flex size-14 items-center justify-center rounded-full ${colours}`}>
        <Icon size={28} />
      </span>
      {children}
    </div>
  );
}

export function InviteAcceptPage() {
  const [searchParams] = useSearchParams();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const inviteToken = searchParams.get('token') ?? '';

  const preview = useQuery({
    ...previewInvitationOptions({ path: { token: inviteToken } }),
    enabled: inviteToken !== '',
    retry: false,
  });
  const accept = useMutation(acceptInvitationMutation());

  if (!inviteToken || preview.isError) {
    return (
      <AuthLayout
        title="This invitation is not valid"
        subtitle="It may have expired or already been used. Ask the person who invited you to send a new one."
      >
        <Outcome tone="danger">
          <Link to="/login" className={buttonStyles({ size: 'lg', className: 'w-full' })}>
            Go to sign in
          </Link>
        </Outcome>
      </AuthLayout>
    );
  }

  if (accept.isSuccess) {
    return (
      <AuthLayout title="Invitation accepted" subtitle="You now have access to the organization.">
        <Outcome tone="success">
          <Link to="/" className={buttonStyles({ size: 'lg', className: 'w-full' })}>
            Open Conflui
          </Link>
        </Outcome>
      </AuthLayout>
    );
  }

  if (!preview.data) {
    return (
      <AuthLayout title="Loading your invitation">
        <div className="flex justify-center text-text-tertiary" role="status">
          <Spinner size="md" />
        </div>
      </AuthLayout>
    );
  }

  const invitation = preview.data;
  const redirect = encodeURIComponent(`/invite/accept?token=${inviteToken}`);
  const details: Array<[string, string]> = [
    ['Organization', invitation.orgName],
    ['Role', ROLE_LABELS[invitation.role]],
    ['Invited by', invitation.inviterName],
    ['Expires', new Date(invitation.expiresAt).toLocaleDateString()],
  ];

  return (
    <AuthLayout title="You are invited" subtitle={`Join ${invitation.orgName} on Conflui`}>
      <div className="flex flex-col gap-6">
        <dl className="divide-y divide-card-border rounded-lg border border-card-border">
          {details.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-4 px-4 py-3 text-sm">
              <dt className="text-text-tertiary">{label}</dt>
              <dd className="font-medium text-text-primary">{value}</dd>
            </div>
          ))}
        </dl>

        {accept.isError && (
          <Alert status="error">
            <AlertIndicator />
            <AlertContent>
              <AlertTitle>{accept.error.message}</AlertTitle>
            </AlertContent>
          </Alert>
        )}

        {isAuthenticated ? (
          <Button
            size="lg"
            className="w-full"
            loading={accept.isPending}
            onClick={() => accept.mutate({ path: { token: inviteToken } })}
          >
            Accept invitation
          </Button>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-center text-sm text-text-tertiary">
              Sign in or create an account to accept this invitation.
            </p>
            <div className="flex gap-3">
              <Link to={`/login?redirect=${redirect}`} className={buttonStyles({ size: 'lg', className: 'flex-1' })}>
                Sign in
              </Link>
              <Link
                to={`/signup?token=${encodeURIComponent(inviteToken)}`}
                className={buttonStyles({ appearance: 'outline', size: 'lg', className: 'flex-1' })}
              >
                Sign up
              </Link>
            </div>
          </div>
        )}
      </div>
    </AuthLayout>
  );
}
